import { Send } from 'lucide-react';
import { useState, type KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { t } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * Compact reply box for a Work Item (Enter sends, Shift+Enter for a new line).
 * Sending wakes the agent; `onSent` lets callers update optimistically.
 */
export function AnswerBox({
  workItemId,
  placeholder = t('Your answer … (Enter to send, Shift+Enter for a new line)'),
  onSent,
  autoFocus,
  className,
}: {
  workItemId: string;
  placeholder?: string;
  onSent?: () => void;
  autoFocus?: boolean;
  className?: string;
}) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setError(null);
    try {
      await api.workItems.sendMessage(workItemId, { content });
      setDraft('');
      onSent?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div className={cn('grid gap-1.5', className)}>
      <div className="flex items-end gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={2}
          className="min-h-11 resize-none bg-card"
          aria-label={t('Your answer')}
          autoFocus={autoFocus}
        />
        <Button onClick={() => void submit()} loading={sending} disabled={!draft.trim()} className="h-11" aria-label={t('Send answer')}>
          {!sending && <Send />}
          <span className="hidden sm:inline">{t('Send')}</span>
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
